import * as React from 'react';
import { Storage } from 'aws-amplify';
import { cn } from '../../lib/utils';

const PAIV_DEFAULT_IMG = '/images/default-avatar.png';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface UserAvatarProps {
  imageName?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
};

export function UserAvatar({
  imageName,
  name = '',
  size = 'md',
  className,
}: UserAvatarProps) {
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const isPaivbot = name?.toLowerCase().match(/paiv|admin-paiv/) !== null;
  const initials = getInitials(name);

  React.useEffect(() => {
    let mounted = true;

    async function loadImage() {
      setIsLoading(true);
      setHasError(false);

      try {
        if (imageName) {
          const url = await Storage.get(imageName);
          if (mounted) setImageUrl(url as string);
        } else if (isPaivbot) {
          if (mounted) setImageUrl(PAIV_DEFAULT_IMG);
        } else {
          if (mounted) setImageUrl(null);
        }
      } catch {
        if (mounted) {
          setHasError(true);
          if (isPaivbot) {
            setImageUrl(PAIV_DEFAULT_IMG);
          } else {
            setImageUrl(null);
          }
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    loadImage();
    return () => {
      mounted = false;
    };
  }, [imageName, isPaivbot]);

  const showImage = imageUrl && !hasError;

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center rounded-full bg-muted font-medium text-foreground overflow-hidden flex-shrink-0',
        sizeClasses[size],
        className
      )}
    >
      {isLoading ? (
        <div className="animate-pulse bg-muted-foreground/20 absolute inset-0" />
      ) : showImage ? (
        <img
          src={imageUrl}
          alt={name || 'User'}
          className="size-full object-cover"
          onError={() => setHasError(true)}
        />
      ) : (
        <span>{initials || '?'}</span>
      )}
    </div>
  );
}
